import React, { useState } from 'react';
import { PlusIcon, Trash2Icon, MinusIcon, XIcon, AlertTriangleIcon, SparklesIcon } from 'lucide-react';
interface Span {
  id: number;
  diameter: string;
  length: string;
  quantity: number;
  hasWarning?: boolean;
  warningMessage?: string;
}
interface SpansTabProps {
  spans: Span[];
  setSpans: (spans: Span[]) => void;
}
export function SpansTab({
  spans,
  setSpans
}: SpansTabProps) {
  const [spanType, setSpanType] = useState<'permapipe' | 'galvanized'>('galvanized');
  const [showSpanSelector, setShowSpanSelector] = useState(false);
  const [showOptimizationPanel, setShowOptimizationPanel] = useState(false);
  const [optimizationMethod, setOptimizationMethod] = useState<'avoid-heavy' | 'avoid-long' | 'economical' | 'best-ratio'>('best-ratio');
  const [spanQuantities, setSpanQuantities] = useState<{
    [key: string]: number;
  }>({});
  const availableSpans = [{
    diameter: 'Elbow',
    length: '0.61'
  }, {
    diameter: 'Spans 8"5/8',
    length: '50.14'
  }, {
    diameter: 'Spans 6"5/8',
    length: '62.03'
  }, {
    diameter: 'Spans 6"5/8',
    length: '56.08'
  }, {
    diameter: 'OH 6"5/8',
    length: '15.50'
  }, {
    diameter: 'Spans 10"3/4',
    length: '65.00'
  }, {
    diameter: 'Spans 12"3/4',
    length: '70.00'
  }];
  const totalSpans = spans.reduce((sum, span) => sum + span.quantity, 0);
  const totalLength = spans.reduce((sum, span) => sum + parseFloat(span.length) * span.quantity, 0).toFixed(2);
  const getSpanKey = (diameter: string, length: string) => `${diameter}-${length}`;
  const getSpanQuantity = (diameter: string, length: string) => {
    const key = getSpanKey(diameter, length);
    return spanQuantities[key] || 1;
  };
  const updateSpanQuantity = (diameter: string, length: string, quantity: number) => {
    const key = getSpanKey(diameter, length);
    setSpanQuantities({
      ...spanQuantities,
      [key]: Math.max(1, quantity)
    });
  };
  const addSpan = (diameter: string, length: string) => {
    const quantity = getSpanQuantity(diameter, length);
    const existingSpan = spans.find(s => s.diameter === diameter && s.length === length);
    if (existingSpan) {
      setSpans(spans.map(s => s.id === existingSpan.id ? {
        ...s,
        quantity: s.quantity + quantity
      } : s));
    } else {
      const newSpan: Span = {
        id: Math.max(...spans.map(s => s.id), 0) + 1,
        diameter,
        length,
        quantity
      };
      setSpans([...spans, newSpan]);
    }
    // Reset quantity for this span
    const key = getSpanKey(diameter, length);
    setSpanQuantities({
      ...spanQuantities,
      [key]: 1
    });
    // Panel stays open - removed setShowSpanSelector(false)
  };
  const increaseQuantity = (id: number) => {
    setSpans(spans.map(span => span.id === id ? {
      ...span,
      quantity: span.quantity + 1
    } : span));
  };
  const decreaseQuantity = (id: number) => {
    setSpans(spans.map(span => span.id === id && span.quantity > 1 ? {
      ...span,
      quantity: span.quantity - 1
    } : span));
  };
  const removeSpan = (id: number) => {
    setSpans(spans.filter(span => span.id !== id));
  };
  const handleCorrectObstacle = (spanId: number) => {
    setSpans(spans.map(span => span.id === spanId ? {
      ...span,
      hasWarning: false,
      warningMessage: undefined
    } : span));
  };
  const handleOptimizeSpans = () => {
    console.log(`Optimizing spans with method: ${optimizationMethod}`);
    // Here you would implement the actual optimization logic
    setShowOptimizationPanel(false);
  };
  return <div className="space-y-6" data-id="element-177">
      {/* Span Type Toggle */}
      <div data-id="element-178">
        <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-179">
          Span Type
        </label>
        <div className="flex gap-2 bg-gray-100 p-1 rounded-lg" data-id="element-180">
          <button onClick={() => setSpanType('galvanized')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${spanType === 'galvanized' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-181">
            Galvanized
          </button>
          <button onClick={() => setSpanType('permapipe')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${spanType === 'permapipe' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-182">
            Permapipe
          </button>
        </div>
      </div>
      {/* Spans Summary */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200" data-id="element-183">
        <div className="flex justify-between items-center mb-2" data-id="element-184">
          <span className="text-sm text-gray-700 font-medium" data-id="element-185">
            Total spans:
          </span>
          <span className="text-lg font-bold text-gray-900" data-id="element-186">{totalSpans}</span>
        </div>
        <div className="flex justify-between items-center" data-id="element-187">
          <span className="text-sm text-gray-700 font-medium" data-id="element-188">
            Total length:
          </span>
          <span className="text-lg font-bold text-blue-600" data-id="element-189">
            {totalLength}m
          </span>
        </div>
      </div>
      {/* Optimize Spans Button */}
      <button onClick={() => setShowOptimizationPanel(!showOptimizationPanel)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors" data-id="element-190">
        <SparklesIcon className="w-4 h-4" data-id="element-191" />
        Recalculate Optimal Spans
      </button>
      {/* Optimization Panel */}
      {showOptimizationPanel && <div className="bg-white border-2 border-purple-500 rounded-lg shadow-lg overflow-hidden" data-id="element-192">
          <div className="bg-purple-50 px-4 py-3 border-b border-purple-200 flex items-center justify-between" data-id="element-193">
            <div className="flex items-center gap-2" data-id="element-194">
              <SparklesIcon className="w-5 h-5 text-purple-600" data-id="element-195" />
              <p className="text-sm font-semibold text-gray-800" data-id="element-196">
                Optimization Settings
              </p>
            </div>
            <button onClick={() => setShowOptimizationPanel(false)} className="text-gray-500 hover:text-gray-700 transition-colors" data-id="element-197">
              <XIcon className="w-4 h-4" data-id="element-198" />
            </button>
          </div>
          <div className="p-4 space-y-4" data-id="element-199">
            <p className="text-sm text-gray-600" data-id="element-200">Select optimization method:</p>
            {/* Optimization Method Options */}
            <div className="space-y-2" data-id="element-201">
              <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors" data-id="element-202">
                <input type="radio" name="optimization" value="avoid-heavy" checked={optimizationMethod === 'avoid-heavy'} onChange={e => setOptimizationMethod(e.target.value as any)} className="mt-0.5 w-4 h-4 text-purple-600 focus:ring-purple-500" data-id="element-203" />
                <div className="flex-1" data-id="element-204">
                  <p className="text-sm font-medium text-gray-900" data-id="element-205">
                    Avoid heavy spans
                  </p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-206">
                    Prioritizes lighter spans for easier installation
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors" data-id="element-207">
                <input type="radio" name="optimization" value="avoid-long" checked={optimizationMethod === 'avoid-long'} onChange={e => setOptimizationMethod(e.target.value as any)} className="mt-0.5 w-4 h-4 text-purple-600 focus:ring-purple-500" data-id="element-208" />
                <div className="flex-1" data-id="element-209">
                  <p className="text-sm font-medium text-gray-900" data-id="element-210">
                    Avoid long spans
                  </p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-211">
                    Uses shorter spans for greater flexibility
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors" data-id="element-212">
                <input type="radio" name="optimization" value="economical" checked={optimizationMethod === 'economical'} onChange={e => setOptimizationMethod(e.target.value as any)} className="mt-0.5 w-4 h-4 text-purple-600 focus:ring-purple-500" data-id="element-213" />
                <div className="flex-1" data-id="element-214">
                  <p className="text-sm font-medium text-gray-900" data-id="element-215">
                    Most economical spans
                  </p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-216">
                    Minimizes total span cost
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 border-2 border-purple-300 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors" data-id="element-217">
                <input type="radio" name="optimization" value="best-ratio" checked={optimizationMethod === 'best-ratio'} onChange={e => setOptimizationMethod(e.target.value as any)} className="mt-0.5 w-4 h-4 text-purple-600 focus:ring-purple-500" data-id="element-218" />
                <div className="flex-1" data-id="element-219">
                  <p className="text-sm font-medium text-gray-900" data-id="element-220">
                    Best target-performance ratio
                  </p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-221">
                    Optimal balance between precision and efficiency
                  </p>
                </div>
              </label>
            </div>
            {/* Action Buttons */}
            <div className="flex gap-2 pt-2" data-id="element-222">
              <button onClick={() => setShowOptimizationPanel(false)} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors" data-id="element-223">
                Cancel
              </button>
              <button onClick={handleOptimizeSpans} className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-224">
                Apply Optimization
              </button>
            </div>
          </div>
        </div>}
      {/* Add Span Button */}
      <button onClick={() => setShowSpanSelector(!showSpanSelector)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors" data-id="element-225">
        <PlusIcon className="w-4 h-4" data-id="element-226" />
        Add Span
      </button>
      {/* Span Selector Dropdown */}
      {showSpanSelector && <div className="bg-white border-2 border-blue-500 rounded-lg shadow-lg overflow-hidden" data-id="element-227">
          <div className="bg-blue-50 px-4 py-2 border-b border-blue-200 flex items-center justify-between" data-id="element-228">
            <p className="text-sm font-medium text-gray-700" data-id="element-229">Select a span:</p>
            <button onClick={() => setShowSpanSelector(false)} className="text-gray-500 hover:text-gray-700 transition-colors" data-id="element-230">
              <XIcon className="w-4 h-4" data-id="element-231" />
            </button>
          </div>
          <div className="max-h-60 overflow-y-auto" data-id="element-232">
            {availableSpans.map((span, index) => {
          const currentQuantity = getSpanQuantity(span.diameter, span.length);
          return <div key={index} className="px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-blue-50 transition-colors" data-id="element-233">
                  <div className="flex items-center justify-between gap-3" data-id="element-234">
                    <div className="flex-1 min-w-0" data-id="element-235">
                      <p className="text-sm font-medium text-gray-900 truncate" data-id="element-236">
                        {span.diameter}
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-237">{span.length}m</p>
                    </div>
                    {/* Quantity Controls */}
                    <div className="flex items-center gap-2" data-id="element-238">
                      <div className="flex items-center gap-1 bg-gray-100 rounded-lg px-2 py-1 border border-gray-200" data-id="element-239">
                        <button onClick={e => {
                    e.stopPropagation();
                    updateSpanQuantity(span.diameter, span.length, currentQuantity - 1);
                  }} disabled={currentQuantity <= 1} className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-240">
                          <MinusIcon className="w-3 h-3" data-id="element-241" />
                        </button>
                        <input type="number" min="1" value={currentQuantity} onChange={e => {
                    e.stopPropagation();
                    updateSpanQuantity(span.diameter, span.length, parseInt(e.target.value) || 1);
                  }} onClick={e => e.stopPropagation()} className="w-10 text-center text-sm font-bold text-gray-900 bg-transparent border-0 focus:outline-none focus:ring-0" data-id="element-242" />
                        <button onClick={e => {
                    e.stopPropagation();
                    updateSpanQuantity(span.diameter, span.length, currentQuantity + 1);
                  }} className="p-1 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-243">
                          <PlusIcon className="w-3 h-3" data-id="element-244" />
                        </button>
                      </div>
                      {/* Add Button */}
                      <button onClick={() => addSpan(span.diameter, span.length)} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors" data-id="element-245">
                        Add
                      </button>
                    </div>
                  </div>
                </div>;
        })}
          </div>
        </div>}
      {/* Spans List - Always Visible */}
      <div className="space-y-2" data-id="element-246">
        <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-247">
          Current spans:
        </h3>
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-id="element-248">
          {spans.map((span, index) => <div key={span.id} className={`flex flex-col gap-2 p-3 bg-white border rounded-lg hover:border-gray-300 transition-colors ${span.hasWarning ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} data-id="element-249">
              <div className="flex items-center gap-2" data-id="element-250">
                {/* Span Number */}
                <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-251">
                  <span className="text-xs font-semibold text-gray-600" data-id="element-252">
                    {index + 1}
                  </span>
                </div>
                {/* Span Details */}
                <div className="flex-1 min-w-0" data-id="element-253">
                  <p className="text-sm font-medium text-gray-900 truncate" data-id="element-254">
                    {span.diameter}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-255">{span.length}m each</p>
                </div>
                {/* Quantity Controls */}
                <div className="flex items-center gap-1 bg-gray-50 rounded-lg px-2 py-1 border border-gray-200" data-id="element-256">
                  <button onClick={() => decreaseQuantity(span.id)} disabled={span.quantity <= 1} className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-257">
                    <MinusIcon className="w-3 h-3" data-id="element-258" />
                  </button>
                  <span className="text-sm font-bold text-gray-900 min-w-[20px] text-center" data-id="element-259">
                    {span.quantity}
                  </span>
                  <button onClick={() => increaseQuantity(span.id)} className="p-1 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-260">
                    <PlusIcon className="w-3 h-3" data-id="element-261" />
                  </button>
                </div>
                {/* Delete Button */}
                <button onClick={() => removeSpan(span.id)} className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-262">
                  <Trash2Icon className="w-4 h-4" data-id="element-263" />
                </button>
              </div>
              {/* Obstacle Warning - Inside the span card */}
              {span.hasWarning && span.warningMessage && <div className="flex items-start gap-2 pt-2 border-t border-red-200" data-id="element-264">
                  <AlertTriangleIcon className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" data-id="element-265" />
                  <div className="flex-1" data-id="element-266">
                    <p className="text-xs text-red-800 mb-2" data-id="element-267">
                      This span crosses an obstacle detected on the terrain
                    </p>
                    <button onClick={() => handleCorrectObstacle(span.id)} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-md transition-colors" data-id="element-268">
                      Correct path
                    </button>
                  </div>
                </div>}
            </div>)}
        </div>
      </div>
    </div>;
}